/**
 * PowerRankings — Weekly JAI League Rankings (v2 redesign)
 *
 * Ranked cards: big mono rank, movement triangle (green up / red down),
 * avatar, name + mono record (+ YOU tag), tier label, and JAI's
 * one-line verdict. Your card gets the blue border + emphasis shadow.
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import ToolNav from '../components/ToolNav'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Page, V2Card, BlueTag } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'

// ── Preview flag ───────────────────────────────────────────────────────────────
const IS_PREVIEW =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('preview') === '1'

// ── Mock data ──────────────────────────────────────────────────────────────────
const PREVIEW_WEEK = 14
const PREVIEW_RANKINGS = [
  {
    rank: 1, prev: 1, name: 'Rim Reapers', record: '10-4', streak: 'W5',
    verdict: "Five straight and counting. At this point the championship isn't a question of if, it's a question of by how much. Every week they make it look easy.",
    tier: 'ELITE',
    isMe: true,
  },
  {
    rank: 2, prev: 3, name: 'Hoop Schemers', record: '7-7', streak: 'W2',
    verdict: "Quietly on a two-game winning streak and climbing. The Haliburton pickup was the move of the season — suddenly every category tightens up. Watch this space.",
    tier: 'CONTENDER',
    isMe: false,
  },
  {
    rank: 3, prev: 2, name: 'Dunks & Dimes', record: '7-7', streak: 'L2',
    verdict: "Fell one spot after back-to-back losses they had no business losing. Their bench is carrying water and their stars are on a collective cold streak. Regression incoming.",
    tier: 'CONTENDER',
    isMe: false,
  },
  {
    rank: 4, prev: 6, name: 'Fast Break FC', record: '7-7', streak: 'W3',
    verdict: "The biggest mover this week. Three in a row after a perfect start/sit week that looked suspiciously good. Either they found the cheat code or the schedule has been kind. Probably both.",
    tier: 'CONTENDER',
    isMe: false,
  },
  {
    rank: 5, prev: 4, name: 'Dunk Dynasty', record: '6-8', streak: 'L1',
    verdict: "Slipping. Their entire gameplan revolves around Giannis going berserk every week, and lately he's been merely great instead of transcendent. That's not going to cut it.",
    tier: 'BUBBLE',
    isMe: false,
  },
  {
    rank: 6, prev: 5, name: 'Playoff or Bust', record: '6-8', streak: 'L3',
    verdict: "Their name ages poorly every week. Three straight losses and the playoffs feel like a distant dream. JAI's diagnosis: too many boom/bust players, zero reliable floor.",
    tier: 'BUBBLE',
    isMe: false,
  },
  {
    rank: 7, prev: 7, name: 'The Benchmarks', record: '5-9', streak: 'W1',
    verdict: "Stopped the bleeding with a narrow win but the math is brutal. They'd need to win out AND get help. JAI gives it a 12% shot. Their waiver wire is their only friend right now.",
    tier: 'LONGSHOT',
    isMe: false,
  },
  {
    rank: 8, prev: 8, name: 'Bench Warmers United', record: '3-11', streak: 'L6',
    verdict: "Six losses in a row and the trade deadline has come and gone without a move. At this point they're not tanking — they're just existing. Hope the draft lottery treats them well.",
    tier: 'ELIMINATED',
    isMe: false,
  },
]

const TIER_COLORS = {
  ELITE:      V2.blue,
  CONTENDER:  V2.success,
  BUBBLE:     V2.warning,
  LONGSHOT:   V2.faint,
  ELIMINATED: V2.error,
}

function MoveIndicator({ rank, prev }) {
  const diff = prev - rank
  if (diff === 0) {
    return <span style={{ fontSize: 11, color: V2.faint, fontFamily: JAI_FONTS.mono, fontWeight: 700 }}>—</span>
  }
  const up = diff > 0
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <svg width="11" height="11" viewBox="0 0 12 12" fill={up ? V2.success : V2.error} style={{ transform: up ? 'none' : 'rotate(180deg)' }}>
        <path d="M6 1l5 7H1l5-7Z" />
      </svg>
      <span style={{ fontSize: 11, fontWeight: 800, color: up ? V2.success : V2.error, fontFamily: JAI_FONTS.mono }}>{Math.abs(diff)}</span>
    </span>
  )
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function PowerRankings() {
  const { id } = useParams()
  const [league, setLeague] = useState(IS_PREVIEW ? { name: 'The Hardwood Cartel' } : null)
  const [loading, setLoading] = useState(!IS_PREVIEW)

  useEffect(() => {
    if (IS_PREVIEW) return
    leaguesApi.get(id).then(r => {
      setLeague(r.data.league)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <span className="text-sm" style={{ color: V2.faint }}>Loading…</span>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <V2Page
        topBar={<ToolNav leagueId={id} />}
        kicker={
          <Link to={`/league/${id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            {league?.name ? `${league.name} · Week ${PREVIEW_WEEK} verdicts` : `Week ${PREVIEW_WEEK} verdicts`}
          </Link>
        }
        title="Power Rankings"
        sub="JAI's weekly read on every team — records lie, rankings don't."
        maxWidth={800}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {PREVIEW_RANKINGS.map(team => (
            <V2Card
              key={team.name}
              pad="15px 20px"
              style={team.isMe ? { border: '1px solid rgba(30,91,214,0.3)', boxShadow: V2.emphasisShadow } : {}}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <span style={{ fontSize: 24, fontWeight: 800, color: team.isMe ? V2.blue : 'rgba(10,31,68,0.25)', fontFamily: JAI_FONTS.mono, width: 36, flexShrink: 0, letterSpacing: '-0.02em' }}>
                  {String(team.rank).padStart(2, '0')}
                </span>
                <span style={{ width: 38, flexShrink: 0, display: 'inline-flex' }}>
                  <MoveIndicator rank={team.rank} prev={team.prev} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: V2.navy }}>{team.name}</span>
                    <span style={{ fontSize: 11.5, color: V2.faint, fontFamily: JAI_FONTS.mono }}>{team.record} · {team.streak}</span>
                    {team.isMe && <BlueTag>You</BlueTag>}
                    <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', color: TIER_COLORS[team.tier] || V2.faint, fontFamily: JAI_FONTS.mono }}>
                      {team.tier}
                    </span>
                  </div>
                  <div style={{ fontSize: 15, color: V2.navy, marginTop: 4, lineHeight: 1.5 }}>{team.verdict}</div>
                </div>
              </div>
            </V2Card>
          ))}
        </div>

        <p style={{ textAlign: 'center', fontSize: 11.5, color: V2.faint, marginTop: 16 }}>
          Rankings refresh every Monday after matchups settle.
        </p>
      </V2Page>
    </AppShell>
  )
}
